'use client';
import Link from '@eliadmin/core/src/components/link';

import { fetchOffers } from '@eliadmin/core/src/api/jobportal/offers-api';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import { GoArrowRight } from 'react-icons/go';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { JobOfferCardSmall } from '../jobportal/job-offer-card-small';
import { JobOfferCardSmallSkeleton } from '../jobportal/job-offer-card-small-skeleton';
import { buildJobOffersQuery } from '@eliadmin/core/src/utils/jobportal-offers-query';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useQuery } from '@tanstack/react-query';
import { CarouselProvider } from '@eliadmin/core/src/components/ui/carousel-context';
import { CarouselControls } from '@eliadmin/core/src/components/ui/carousel-controls';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';

export function TopOffers({ locale }: { locale: string }) {
  const { me, isInitialized } = useCustomerStore();

  const { data: toppedOffers, isLoading: isLoadingTopped } = useQuery({
    queryKey: [CacheKey.JOBPPORTAL_OFFERS, 'topped'],
    queryFn: () =>
      fetchOffers({
        locale,
        query: {
          ...buildJobOffersQuery({
            is_paid: true,
            topped_up_to: Date.now().toString(),
            expires_at: true,
          }),
          'order[]': ['desc', 'desc'],
          'orderBy[]': ['topped_up_to', 'published_at'],
          results: 12,
        },
        authToken: me?.authToken,
      }),
    enabled: isInitialized,
  });

  const { data: offersNotTopped, isLoading: isLoadingNotTopped } = useQuery({
    queryKey: [CacheKey.JOBPPORTAL_OFFERS, 'not_topped'],
    queryFn: () =>
      fetchOffers({
        locale,
        query: {
          ...buildJobOffersQuery({ is_paid: true, expires_at: true }),
          'order[]': ['desc'],
          'orderBy[]': ['published_at'],
          results: 24,
        },
        authToken: me?.authToken,
      }),
    enabled: isInitialized,
  });

  return (
    <CarouselProvider>
      <section id="top-offers" className={cn('custom-section', 'py-8 sm:py-12 lg:py-16')}>
        <div className={cn('custom-container', 'flex flex-col items-center justify-center gap-8')}>
          <div className="flex w-full items-center justify-between px-6 md:px-0">
            <h2 className="text-2xl font-bold text-foreground sm:text-3xl">Pracovné ponuky</h2>
            <div className="hidden items-center justify-end gap-4 sm:flex">
              <div className="hidden lg:block">
                <Button asChild>
                  <Link prefetchOnHover href={'/ponuky'}>
                    Všetky pracovné ponuky
                    <GoArrowRight size={24} className="text-white" />
                  </Link>
                </Button>
              </div>
              <CarouselControls
                className={{
                  main: 'gap-4',
                  next: 'flex min-h-12 min-w-12 items-center justify-center border border-[#D8D8DB] text-primary hover:bg-transparent hover:text-primary',
                  prev: 'flex min-h-12 min-w-12 items-center justify-center border border-[#D8D8DB] text-primary hover:bg-transparent hover:text-primary',
                }}
              />
            </div>
          </div>

          <Carousel
            showDots={true}
            className={{
              dots: '-bottom-[52px]',
              main: 'w-full overflow-visible',
              container: 'w-full gap-2 overflow-visible',
              wrapper: 'w-full overflow-visible',
              slide: 'w-full max-w-[405px] md:w-[50%] lg:w-[33%] xl:w-[24%]',
            }}
            slides={
              !isInitialized || isLoadingTopped
                ? Array.from({ length: 4 }).map((_, index) => (
                    <JobOfferCardSmallSkeleton key={`topped-skel-${index}`} />
                  ))
                : (toppedOffers?.data ?? []).map(offer => (
                    <JobOfferCardSmall key={offer.slug} offer={offer} />
                  ))
            }
          />

          <div className="mt-4 grid w-full grid-cols-1 items-center justify-center gap-0 gap-y-4 sm:grid-cols-2 md:gap-2 lg:grid-cols-3 xl:grid-cols-4">
            {!isInitialized || isLoadingNotTopped
              ? Array.from({ length: 4 }).map((_, index) => (
                  <JobOfferCardSmallSkeleton key={index} />
                ))
              : (offersNotTopped?.data ?? [])
                  .filter(
                    offer =>
                      !offer.topped_up_to || new Date(offer.topped_up_to).getTime() < Date.now(),
                  )
                  .map(
                    (offer, index) =>
                      index < 8 && <JobOfferCardSmall key={offer.slug} offer={offer} />,
                  )}
          </div>
        </div>
      </section>
    </CarouselProvider>
  );
}
